import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Binoculars,
  MagnifyingGlass,
  Eye,
  Package,
  CheckSquare,
  Square,
  DownloadSimple,
  Boat,
  Airplane,
  Truck,
  ArrowRight,
  Clock,
  Star,
  Spinner,
  Warning,
  X,
  Prohibit,
} from '@phosphor-icons/react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../../services/api'
import { parseDate, daysUntil } from '../../lib/date'

interface Opportunity {
  id: string
  txControl: string
  clientReference?: string
  status: string
  incoterm: string
  originCity: string
  originState: string
  originCountry: string
  originPort: string | null
  destinationCity: string
  destinationState: string
  destinationCountry: string
  destinationPort: string | null
  goodsDescription: string
  goodsType: string
  volumeItems: any[]
  createdAt: string
  modalityId: string
  modalityName: string
  modalityType: string
  volumeType: string
  shipperName: string
  shipperTradingName: string
  totalOffersCount: number
  myOffersCount: number
  quoteDeadline: string | null
}

export default function OpportunitiesPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [loading, setLoading] = useState(true)
  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [error, setError] = useState<string | null>(null)
  const [warningMessage, setWarningMessage] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [filterModality, setFilterModality] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [selectedOpportunities, setSelectedOpportunities] = useState<string[]>([])

  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  // Decline opportunity (Plane #3)
  const [declineTarget, setDeclineTarget] = useState<string | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [declineLoading, setDeclineLoading] = useState(false)
  const limit = 20

  // Check for redirect message
  useEffect(() => {
    if (location.state?.message) {
      setWarningMessage(location.state.message)
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    fetchOpportunities()
  }, [page, filterModality, filterType])

  async function fetchOpportunities() {
    try {
      setLoading(true)
      setError(null)

      const queryParams = new URLSearchParams()
      queryParams.set('page', String(page))
      queryParams.set('limit', String(limit))
      if (filterModality !== 'all') queryParams.set('modality', filterModality)
      if (filterType !== 'all') queryParams.set('type', filterType)
      if (searchQuery) queryParams.set('search', searchQuery)

      const res = await api.get(`/freight-forwarder/opportunities?${queryParams.toString()}`)

      setOpportunities(res.data.opportunities)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch (err: any) {
      console.error('Error fetching opportunities:', err)
      setError(err.response?.data?.error || 'Erro ao carregar oportunidades')
    } finally {
      setLoading(false)
    }
  }

  // Decline an opportunity (Plane #3). Permanent for this agent; the quote
  // drops from the list. Reason is optional and kept for the manager.
  async function handleDeclineConfirm() {
    if (!declineTarget) return
    try {
      setDeclineLoading(true)
      await api.post(`/freight-forwarder/opportunities/${declineTarget}/decline`, {
        reason: declineReason || undefined,
      })
      setOpportunities((prev) => prev.filter((o) => o.id !== declineTarget))
      setTotal((t) => Math.max(0, t - 1))
      setDeclineTarget(null)
      setDeclineReason('')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao declinar oportunidade')
    } finally {
      setDeclineLoading(false)
    }
  }

  // Search on Enter
  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(1)
      fetchOpportunities()
    }
  }

  // Filter functionality (client-side for search)
  const filteredOpportunities = opportunities.filter((opp) => {
    if (!searchQuery) return true
    const matchesSearch =
      opp.txControl?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.clientReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.shipperName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.goodsDescription?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedOpportunities.length === filteredOpportunities.length) {
      setSelectedOpportunities([])
    } else {
      setSelectedOpportunities(filteredOpportunities.map((o) => o.id))
    }
  }

  const toggleSelectOpportunity = (oppId: string) => {
    setSelectedOpportunities((prev) =>
      prev.includes(oppId) ? prev.filter((id) => id !== oppId) : [...prev, oppId]
    )
  }

  const isOpportunitySelected = (oppId: string) => selectedOpportunities.includes(oppId)
  const isAllSelected =
    filteredOpportunities.length > 0 &&
    selectedOpportunities.length === filteredOpportunities.length

  // Get modality badge
  const getModalityBadge = (modalityName: string) => {
    const name = modalityName?.toLowerCase() || ''
    let icon = <Package size={12} />
    let color = 'text-gray-600'
    let label = modalityName || 'Outro'

    if (name.includes('mar')) {
      icon = <Boat size={12} />
      color = 'text-blue-600'
      label = 'Marítimo'
    } else if (name.includes('aer') || name.includes('air')) {
      icon = <Airplane size={12} />
      color = 'text-purple-600'
      label = 'Aéreo'
    } else if (name.includes('ter') || name.includes('rod')) {
      icon = <Truck size={12} />
      color = 'text-green-600'
      label = 'Terrestre'
    }

    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
        {icon}
        {label}
      </span>
    )
  }

  // Get type badge
  const getTypeBadge = (type: string) => {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        {type === 'import' ? 'Importação' : type === 'export' ? 'Exportação' : type}
      </span>
    )
  }

  // Calculate total volume
  const getTotalVolume = (volumeItems: any[]) => {
    if (!volumeItems || !Array.isArray(volumeItems) || volumeItems.length === 0) return null
    const totalQty = volumeItems.reduce((acc, item) => acc + (item.quantity || 1), 0)
    return `${totalQty} ${volumeItems.length > 1 ? 'itens' : 'item'}`
  }

  // Time ago
  const timeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Agora'
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays === 1) return '1d'
    return `${diffDays}d`
  }

  // Check if deadline has passed (considers end of day)
  const isDeadlineExpired = (deadline: string | null) => {
    if (!deadline) return false
    const days = daysUntil(deadline)
    return days < 0
  }

  // Get deadline info
  const getDeadlineInfo = (deadline: string | null) => {
    if (!deadline) return null

    const days = daysUntil(deadline)

    if (days < 0) {
      return { text: 'Expirado', color: 'text-red-600 bg-red-50', expired: true }
    }
    if (days === 0) {
      return { text: 'Hoje', color: 'text-amber-600 bg-amber-50', expired: false }
    }
    if (days === 1) {
      return { text: '1 dia', color: 'text-amber-600 bg-amber-50', expired: false }
    }
    if (days <= 3) {
      return { text: `${days} dias`, color: 'text-amber-600 bg-amber-50', expired: false }
    }
    return { text: `${days} dias`, color: 'text-gray-600 bg-gray-50', expired: false }
  }

  // Calculate metrics
  const metrics = {
    overview: {
      total: total,
      thisWeek: opportunities.filter(
        (o) =>
          new Date(o.createdAt).getTime() > new Date().getTime() - 7 * 24 * 60 * 60 * 1000
      ).length,
    },
    byModality: {
      maritime: opportunities.filter((o) => o.modalityName?.toLowerCase().includes('mar')).length,
      air: opportunities.filter((o) => o.modalityName?.toLowerCase().includes('aer')).length,
      ground: opportunities.filter((o) => o.modalityName?.toLowerCase().includes('ter')).length,
    },
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Oportunidades</h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedOpportunities.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedOpportunities.length}</span>
                  selecionada{selectedOpportunities.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Warning Message */}
      {warningMessage && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Warning size={20} className="text-amber-600" weight="fill" />
                <span className="text-amber-800 text-sm">{warningMessage}</span>
              </div>
              <button
                onClick={() => setWarningMessage(null)}
                className="text-amber-600 hover:text-amber-800"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Zone - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Overview Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Visão Geral
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{metrics.overview.total}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {metrics.overview.thisWeek} esta semana
                </p>
              </div>
              <Binoculars size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Modalities Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Por Modalidade
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Boat size={14} className="text-blue-600" />
                  Marítimo
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.maritime}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Airplane size={14} className="text-purple-600" />
                  Aéreo
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.air}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Truck size={14} className="text-green-600" />
                  Terrestre
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.ground}</span>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Dica
            </p>
            <p className="text-sm text-gray-600">
              Clique em "Propor" para enviar sua oferta para uma cotação.
              Cotações com o ícone <Star size={12} className="inline text-amber-500" weight="fill" /> já possuem sua proposta.
            </p>
          </div>
        </div>

        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por empresa, número ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Modality Filter */}
            <select
              value={filterModality}
              onChange={(e) => {
                setFilterModality(e.target.value)
                setPage(1)
              }}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todas Modalidades</option>
              <option value="maritimo">Marítimo</option>
              <option value="aereo">Aéreo</option>
              <option value="terrestre">Terrestre</option>
            </select>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value)
                setPage(1)
              }}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos Tipos</option>
              <option value="import">Importação</option>
              <option value="export">Exportação</option>
            </select>

            {/* Search Button */}
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9"
              onClick={() => {
                setPage(1)
                fetchOpportunities()
              }}
            >
              <MagnifyingGlass size={16} />
              Buscar
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando oportunidades...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => fetchOpportunities()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Data Grid */}
        {!loading && !error && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center w-full"
                      >
                        {isAllSelected ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cotação
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rota
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modalidade
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Incoterm
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ofertas
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criada
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prazo
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOpportunities.map((opp) => (
                    <tr
                      key={opp.id}
                      className={`group hover:bg-gray-50 transition-colors ${
                        isOpportunitySelected(opp.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelectOpportunity(opp.id)}
                          className="flex items-center justify-center"
                        >
                          {isOpportunitySelected(opp.id) ? (
                            <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                          ) : (
                            <Square size={18} className="text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {opp.txControl}
                            </p>
                            {Number(opp.myOffersCount) > 0 && (
                              <Star size={14} weight="fill" className="text-amber-500" title="Você já enviou uma proposta" />
                            )}
                          </div>
                          {opp.clientReference && (
                            <p className="text-xs text-emerald-600 truncate max-w-[200px]">
                              Ref: {opp.clientReference}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">{opp.shipperName}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 leading-tight">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium w-3">DE</span>
                            <span className="truncate">{opp.originCity}, {opp.originCountry}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium w-3">P/</span>
                            <span className="truncate">{opp.destinationCity}, {opp.destinationCountry}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getModalityBadge(opp.modalityName)}
                        <div className="text-xs text-gray-500 mt-1">{getTypeBadge(opp.modalityType)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">{opp.incoterm}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {opp.totalOffersCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-600">{timeAgo(opp.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {opp.quoteDeadline ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getDeadlineInfo(opp.quoteDeadline)?.color}`}>
                            {getDeadlineInfo(opp.quoteDeadline)?.text}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 sticky right-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] ${isOpportunitySelected(opp.id) ? 'bg-blue-50' : 'bg-white'} group-hover:bg-gray-50`}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/freight-forwarder/opportunities/${opp.id}`)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye size={16} className="text-gray-600" />
                          </Button>
                          {Number(opp.myOffersCount) === 0 ? (
                            isDeadlineExpired(opp.quoteDeadline) ? (
                              <Button
                                size="sm"
                                disabled
                                className="bg-gray-300 text-gray-500 text-xs px-3 cursor-not-allowed"
                              >
                                Expirado
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setDeclineReason(''); setDeclineTarget(opp.id) }}
                                  className="text-xs px-3 text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  Declinar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/freight-forwarder/opportunities/${opp.id}/new-offer`)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-xs px-3"
                                >
                                  Propor
                                </Button>
                              </>
                            )
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/freight-forwarder/opportunities/${opp.id}`)}
                              className="text-xs px-3"
                            >
                              Ver Proposta
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredOpportunities.length === 0 && !loading && (
              <div className="text-center py-12 bg-gray-50">
                <Binoculars size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                <p className="text-gray-500 text-sm">Nenhuma oportunidade encontrada</p>
              </div>
            )}

            {/* Footer with pagination */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{total}</span> oportunidades
                {selectedOpportunities.length > 0 && (
                  <>
                    {' '}
                    · <span className="font-medium">{selectedOpportunities.length}</span> selecionada
                    {selectedOpportunities.length > 1 ? 's' : ''}
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Decline Opportunity Dialog (Plane #3) */}
      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <Prohibit size={24} weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Declinar Oportunidade</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Esta cotação deixará de aparecer nas suas oportunidades. O motivo é
              opcional e serve apenas para controle interno da sua equipe.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
            <Input
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Ex: fora da nossa rota, sem espaço, tarifa inviável..."
              className="mb-5"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeclineTarget(null); setDeclineReason('') }}
                disabled={declineLoading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <Button
                onClick={handleDeclineConfirm}
                disabled={declineLoading}
                className="bg-red-600 hover:bg-red-700 text-sm"
              >
                {declineLoading ? 'Declinando...' : 'Declinar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
